import { createContext, useContext, useState } from "react";
import { JwtUser, UserDto } from "./types/user.types";
import { extractUserClaimsFromJwt } from "./common/jwt";

interface Context {
  authenticatedUser: JwtUser | null;
  setAuthenticatedUser(userDto: UserDto | null): void;
}

const AuthContext = createContext<Context>(null!);

export const AuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<JwtUser | null>(null);

  const getAuthenticatedUser = () => {
    if (user === null) {
      const token = localStorage.getItem("accessToken");
      if (!token) return null;

      const claims = extractUserClaimsFromJwt(token);
      if (claims) {
        const jwtUser: JwtUser = {
          sub: claims.sub,
          name: claims.name,
          roleId: claims.roleId,
          accessToken: token,
        };
        setUser(jwtUser);
        return jwtUser;
      }
    }

    return user;
  };

  const context: Context = {
    authenticatedUser: getAuthenticatedUser(),
    setAuthenticatedUser(userDto: UserDto | null) {
      if (userDto == null) {
        setUser(null);
        localStorage.removeItem("accessToken");
        return;
      }

      // Extract JWT user from the accessToken returned by the backend
      const token = userDto.accessToken;
      const claims = extractUserClaimsFromJwt(token);

      if (claims) {
        const jwtUser: JwtUser = {
          sub: claims.sub,
          name: claims.name,
          roleId: claims.roleId,
          accessToken: token,
        };
        setUser(jwtUser);
        // Store ONLY the accessToken, not the user object
        localStorage.setItem("accessToken", token);
      }
    },
  };

  return <AuthContext.Provider value={context} children={children} />;
};

export function useAuthContext() {
  return useContext(AuthContext) || {};
}
